import {
  ISOFormatWithSec,
  MaxDate,
  MinDate,
  TimeSpanMaxValue,
  UTCFormatWithSec,
  UTCFormatWithSecZ
} from 'mbs-ui-shared@constants/c-sharp-constants';
import { PatternNameWithId, windowsUpperCaseInvalidPath } from 'mbs-ui-shared@constants/folders-path-constants';
import Computer from 'mbs-ui-shared@models/Computer';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { ScheduleStepValue, ScheduleType } from 'mbs-ui-shared@models/backup/schedule-models';
import {
  baseDateFormatWithSeconds,
  cobbledDateFormat,
  dateTimePointsFormat,
  dateTimePointsReg,
  getFullDateFromDateTimeObject,
  getFullISODateFromDateTimeObject,
  GetTimeSpanString
} from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { PlanDataFromStepsHelper } from 'wizards/helpers/plan-data-from-steps-helper/plan-data-from-steps-helper';
import { AzureInstanceStepValue, DisabledBootDiagnostic } from 'wizards/models/azure-instance-models';
import { BackupToRestoreStepValue } from 'wizards/models/backup-to-restore-models';
import { GlacierRestoreTiers } from 'wizards/models/base/base-models/plan-glacier-restor-tiers-model';
import { RestorePlanType } from 'wizards/models/base/base-models/plan-restore-plan-type-model';
import { RestoreToAzureArgs } from 'wizards/models/base/base-models/plan-restore-to-azure-args-model';
import { RestoreToEC2Args } from 'wizards/models/base/base-models/plan-restore-to-ec2-args-model';
import { RestoreWizardSettings } from 'wizards/models/base/base-plan-models';
import { DefaultTemplate, DefaultTemplateMask, DestinationDBStepValue } from 'wizards/models/destination-db-models';
import { DestinationVMStepValue, MachineFormatInPlan } from 'wizards/models/destination-vm-models';
import { PartitionsStepValue } from 'wizards/models/partitions-models';
import { PlanNameStepValue } from 'wizards/models/plan-name-models';
import { RestoreDisksStepValue } from 'wizards/models/restore-disks-models';
import { RestorePointStepValue, RestoreType } from 'wizards/models/restore-point-models';
import { ScheduleAdvancedStepValue } from 'wizards/models/schedule-advanced-models';
import { HostTypeNumbers, SelectHostStepValue } from 'wizards/models/select-host-models';
import { VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import { ComputerToRestoreStepValue } from 'wizards/models/selected-computers-models';
import { SQLServerInstanceStepValue } from 'wizards/models/sql-server-instance-models';
import { ECInstanceStepValue } from 'wizards/models/target-or-temporary-instance-models';
import {
  AzureRestoreTypeEnum,
  EC2RestoreTypeEnum,
  RestoreAsDiskEnum,
  RestoreTypeFromAgentEnum,
  TypeDataStepValue,
  VirtualDiskFileFormat
} from 'wizards/models/type-data-models';
import { ProvisionModeEnum, VmSettingsStepValue } from 'wizards/models/vm-settings-models';
import { DeletePlan } from '../../models/base/base-models/plan-restore-delete-plan-model';
import { DestinationStepValue } from '../../models/destination-models';
import { EncryptionOptionsStepValue } from '../../models/encryption-options-models';
import { RestoreSourceStepValue } from '../../models/restore-source-models';
import { SupportMethodsForPlanFromSteps } from '../support-methods-for-plan-from-steps';

export class RestorePlanFromStepsHelper {
  public static updateNewPlanByNameStep(newPlan, step: PlanNameStepValue): void {
    newPlan.Name = step.Name;
    newPlan.DeletePlan = step.SavePlanInCloud ? DeletePlan.Never : DeletePlan.OnSucceeds;
    newPlan.SavePlanInCloud = false;
  }

  public static getFileFormat(newPlan, step: TypeDataStepValue): VirtualDiskFileFormat {
    return newPlan.VirtualDiskFileFormatList && newPlan.VirtualDiskFileFormatList.length
      ? newPlan.VirtualDiskFileFormatList.find((f) => f.DisplayName === step.selectedFileFormat)
      : SupportMethodsForPlanFromSteps.getDiskImageFileFormat(step.selectedFileFormat);
  }

  public static updateNewPlanByTypeDataStep(newPlan, step: TypeDataStepValue, type?: PlanTypes): void {
    const isHyperV = type === PlanTypes.RestoreHyperVToCustom || type === PlanTypes.RestoreHyperV;

    switch (step.restoreAs) {
      case RestoreAsDiskEnum.Physical:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.PhysicalDisk;
        newPlan.DiskImageFileFormat = {
          DisplayName: 'Hyper-V Virtual Disk (VHDX-format) dynamic',
          Name: 'VHDX',
          UseBuilder: true,
          VariantName: 'dynamic'
        };
        break;
      case RestoreAsDiskEnum.Virtual:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.VirtualDisk;
        if (isHyperV || type === PlanTypes.RestoreVMware || type === PlanTypes.RestoreVMwareToCustom) {
          newPlan.RestorePlanType = RestorePlanType.VMToCloud;
          newPlan.HostType = isHyperV ? HostTypeNumbers.HyperV : HostTypeNumbers.VMWare;
          newPlan.RestoreToVirtualDisk = {
            DiskImageFileFormat: this.getFileFormat(newPlan, step),
            ExcludeDisks: [],
            GenericDiskAdapterType: 0
          };
        } else {
          newPlan.DiskImageFileFormat = this.getFileFormat(newPlan, step);
        }
        break;
      case RestoreAsDiskEnum.InstantRestoreToHyperV:
      case RestoreAsDiskEnum.RestoreToHyperV:
        if (type === PlanTypes.RestoreDiskImagePlan) {
          newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToHyperV;
        } else {
          newPlan.RestorePlanType = RestorePlanType.HyperV;
          newPlan.ImportVm = false;
        }

        break;
      case RestoreAsDiskEnum.RestoreToHyperVAndImport:
        newPlan.RestorePlanType = RestorePlanType.HyperV;
        newPlan.ImportVm = true;
        break;
      case RestoreAsDiskEnum.RestoreToVMwareVSphere:
        if (type === PlanTypes.RestoreDiskImagePlan) {
          newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToVMware;
        }
        break;
      case RestoreAsDiskEnum.RestoreToVMware:
        if (type === PlanTypes.RestoreDiskImagePlan) {
          newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToVMware;
        } else {
          newPlan.RestorePlanType = RestorePlanType.VMwareMachine;
        }

        break;
      case RestoreAsDiskEnum.InstantRestoreToProxmox:
      case RestoreAsDiskEnum.RestoreToProxmox:
        if (type === PlanTypes.RestoreDiskImagePlan) {
          newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToProxmox;
        } else {
          newPlan.RestorePlanType = RestorePlanType.Proxmox;
        }

        break;
      case RestoreAsDiskEnum.EC2:
        this.updateRestoreToEC2(newPlan, step);
        break;
      case RestoreAsDiskEnum.EBSVolume:
        newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToEC2;
        if (!newPlan.RestoreToEC2) newPlan.RestoreToEC2 = new RestoreToEC2Args();
        newPlan.RestoreToEC2.EC2RestoreType = EC2RestoreTypeEnum.EC2Volume;
        break;
      case RestoreAsDiskEnum.AVM:
        this.updateBaseRestoreToAzure(newPlan);
        newPlan.RestoreToAzure.AzureRestoreType = AzureRestoreTypeEnum.AzureInstance;
        break;
      case RestoreAsDiskEnum.ADD:
        this.updateBaseRestoreToAzure(newPlan);
        newPlan.RestoreToAzure.AzureRestoreType = AzureRestoreTypeEnum.AzureDataDisk;
        break;
    }
  }

  public static updatePlanFromAzureTargetStep(plan, step: AzureInstanceStepValue, compName: string): void {
    if (!plan.RestoreToAzure) plan.RestoreToAzureTemporaryInstance = {};

    plan.RestoreToAzure.RemoteRestorePrefix = compName;
    plan.RestoreToAzure.AzureVMConnectionID = step.connectionId;
    plan.RestoreToAzure.InstanceName = step.computerName;
    plan.RestoreToAzure.Location = step.location;
    plan.RestoreToAzure.ResourceGroupName = step.resourceGroup;
    plan.RestoreToAzure.VMSize = step.vmSize;
    plan.RestoreToAzure.NetworkName = step.network;
    plan.RestoreToAzure.SubnetName = step.subnetName;
    plan.RestoreToAzure.StorageName = step.storage;
    plan.RestoreToAzure.BucketName = step.container;
    plan.RestoreToAzure.BootDiagnosticStorageName =
      step.bootDiagnosticStorage === DisabledBootDiagnostic.id ? '' : step.bootDiagnosticStorage;
    plan.RestoreToAzure.IsRemoteRestore = true;
  }

  public static updatePlanFromAzureTemporaryStep(plan, step: AzureInstanceStepValue): void {
    if (!plan.RestoreToAzureTemporaryInstance) plan.RestoreToAzureTemporaryInstance = {};

    plan.RestoreToAzureTemporaryInstance.AzureVMConnectionID = step.connectionId;
    plan.RestoreToAzureTemporaryInstance.Location = step.location;
    plan.RestoreToAzureTemporaryInstance.ResourceGroupName = step.resourceGroup;
    plan.RestoreToAzureTemporaryInstance.VMSize = step.vmSize;
    plan.RestoreToAzureTemporaryInstance.NetworkName = step.network;
    plan.RestoreToAzureTemporaryInstance.SubnetName = step.subnetName;
    plan.RestoreToAzureTemporaryInstance.StorageName = step.storage;
    plan.RestoreToAzureTemporaryInstance.BucketName = step.container;
    plan.RestoreToAzureTemporaryInstance.BootDiagnosticStorageName =
      step.bootDiagnosticStorage === DisabledBootDiagnostic.id ? '' : step.bootDiagnosticStorage;
    plan.RestoreToAzureTemporaryInstance.IsRemoteRestore = true;
  }

  public static updatePlanFromEC2Target(plan, step: ECInstanceStepValue, compName: string, bootMode: string): void {
    if (!plan.RestoreToEC2) plan.RestoreToEC2 = {};

    plan.Bucket = step.tempDestination;

    plan.RestoreToEC2.BootMode = bootMode;
    plan.RestoreToEC2.RemoteRestorePrefix = compName;
    plan.RestoreToEC2.EC2ConnectionId = step.connectionId;
    plan.RestoreToEC2.IntermediateS3ConnectionId = step.connectionId;
    plan.RestoreToEC2.RegionName = step.region;
    plan.RestoreToEC2.InstanceType = step.instanceType;
    plan.RestoreToEC2.SubnetId = step.subnetId;
    plan.RestoreToEC2.SecurityGroupId = step.securityGroup;
    plan.RestoreToEC2.AvailabilityZone = step.availabilityZone;
    plan.RestoreToEC2.IsRemoteRestore = true;
  }

  public static updatePlanFromEC2TemporaryStep(plan, step: ECInstanceStepValue, compName: string): void {
    if (!plan.RestoreToEC2TemporaryInstance) plan.RestoreToEC2TemporaryInstance = {};

    plan.RestoreToEC2TemporaryInstance.RemoteRestorePrefix = compName;
    plan.RestoreToEC2TemporaryInstance.EC2ConnectionId = step.connectionId;
    plan.RestoreToEC2TemporaryInstance.IntermediateS3ConnectionId = step.connectionId;
    plan.RestoreToEC2TemporaryInstance.RegionName = step.region;
    plan.RestoreToEC2TemporaryInstance.InstanceType = step.instanceType;
    plan.RestoreToEC2TemporaryInstance.SubnetId = step.subnetId;
    plan.RestoreToEC2TemporaryInstance.SecurityGroupId = step.securityGroup;
    plan.RestoreToEC2TemporaryInstance.AMI = step.ami;

    plan.RestoreToEC2TemporaryInstance.AvailabilityZone = step.availabilityZone;
    plan.RestoreToEC2TemporaryInstance.IsRemoteRestore = true;
  }

  public static updateNewPlanByPartitionsStep(newPlan, partitionsStep: PartitionsStepValue): void {
    newPlan.DiskInfo =
      partitionsStep.restoreParentPartitions && partitionsStep.restoreParentPartitions.length
        ? partitionsStep.restoreParentPartitions.sort((a, b) => a.diskNumber - b.diskNumber)
        : [];
    newPlan.ConvertToMBR = partitionsStep.convertToMBR;
  }

  public static updateNewPlanByDestinationStep(
    newPlan,
    step: DestinationStepValue,
    partitionsStep: PartitionsStepValue,
    forIBB = true,
    needRestoreDeleted = true
  ): void {
    if (forIBB) {
      this.updateNewIbbPlanByDestinationStep(newPlan, step);

      const newDisks: any[] = this.getDisksFromRestorePartitions(newPlan, step, partitionsStep);

      newPlan.RestoreDiskConfiguration = newDisks;

      return;
    }

    this.updateNewNotIbbPlanByDestinationStep(newPlan, step, needRestoreDeleted);
  }

  public static updateNewPlanByEncryptionOptionsStep(newPlan, step: EncryptionOptionsStepValue): void {
    if (!(step.isCryptWithPassword === null || step.isCryptWithPassword === undefined)) {
      newPlan.UseEncryption = step.isCryptWithPassword;
      if (newPlan.UseEncryption) {
        SupportMethodsForPlanFromSteps.updatePlanPassword(newPlan, step.password);
      } else {
        delete newPlan.EncryptionPassword;
      }
    } else {
      newPlan.UseEncryption = !!step.password;
      SupportMethodsForPlanFromSteps.updatePlanPassword(newPlan, step.password);
    }
  }

  public static updateNewPlanByRestorePointDBStep(newPlan, step: RestorePointStepValue, timeZoneOffset: number): void {
    newPlan.RestoreType = step.restoreType;

    if (step.restoreType === RestoreType.LatestVersion) {
      newPlan.PointInTime = MaxDate;
      return;
    }

    newPlan.PointInTime = moment
      .utc(getFullISODateFromDateTimeObject(step.fromDateTime))
      .utcOffset(timeZoneOffset * -1)
      .format(UTCFormatWithSec);
    newPlan.UseBackupDate = false;
    newPlan.RestoreFromPointInTime = MinDate;
  }

  public static updateNewPlanByRestorePointStep(
    newPlan,
    step: RestorePointStepValue,
    settings: WizardHelpersSettings,
    isIbb = false
  ): void {
    if (step.restoreType === RestoreType.Manually) {
      newPlan.RestoreType = step.restoreType;
      newPlan.UseBackupDate = false;
      newPlan.RestoreFromPointInTime = MinDate;
      newPlan.PointInTime = isIbb ? moment(getFullDateFromDateTimeObject(step.fromDateTime, true)).format(ISOFormatWithSec) : MaxDate;
    } else if (step.restoreType === RestoreType.LatestVersion) {
      newPlan.RestoreType = step.restoreType;
      newPlan.PointInTime = MaxDate;
    } else if (step.restoreType === RestoreType.PointInTime) {
      newPlan.RestoreType = step.restoreType;
      if (settings.isNBF) {
        newPlan.PointInTime = moment(getFullDateFromDateTimeObject(step.fromDateTime, true)).format(ISOFormatWithSec);
      } else {
        newPlan.PointInTime = moment
          .utc(getFullISODateFromDateTimeObject(step.fromDateTime))
          .utcOffset(settings.timeZoneOffset * -1)
          .format(UTCFormatWithSec);
      }
      newPlan.UseBackupDate = false;
      newPlan.RestoreFromPointInTime = MinDate;
    } else if (step.restoreType === RestoreType.ModificationPeriod || step.restoreType === RestoreType.BackupPeriod) {
      newPlan.RestoreType = RestoreType.PointInTime;
      if (settings.isNBF) {
        newPlan.PointInTime = moment(getFullDateFromDateTimeObject(step.toDateTime, true)).format(ISOFormatWithSec);
        newPlan.RestoreFromPointInTime = moment(getFullDateFromDateTimeObject(step.fromDateTime, true)).format(ISOFormatWithSec);
      } else {
        newPlan.PointInTime = moment
          .utc(getFullISODateFromDateTimeObject(step.toDateTime))
          .utcOffset(settings.timeZoneOffset * -1)
          .format(UTCFormatWithSec);
        newPlan.RestoreFromPointInTime = moment
          .utc(getFullISODateFromDateTimeObject(step.fromDateTime))
          .utcOffset(settings.timeZoneOffset * -1)
          .format(UTCFormatWithSec);
      }
      newPlan.UseBackupDate = step.restoreType === RestoreType.BackupPeriod;
    }
    if (settings.isUnix && !settings.isNBF) newPlan.SyncBeforeRun = !!step.syncBeforeRun;
  }

  public static updateRestoreSourceDB(newPlan, databases: RestoreSourceStepValue, isUnix = false): void {
    const join: '/' | '\\' = isUnix ? '/' : '\\';

    newPlan.Databases = [];

    databases.folders.forEach((path: string) => {
      const pathArr = path.split(join);
      const lastIndex = pathArr.length > 3 ? 2 : pathArr.length - 1;

      newPlan.Databases.push({ InstanceName: pathArr.slice(0, lastIndex).join(join), DatabaseName: pathArr.slice(lastIndex).join(join) });
    });
  }

  public static updateRestoreSourceVM(
    newPlan,
    machines: RestoreSourceStepValue,
    disks: RestoreDisksStepValue,
    type: PlanTypes,
    isCbf: boolean
  ): void {
    const cbfManually = isCbf && newPlan.RestoreType === RestoreType.Manually && !!machines.point;

    if (cbfManually) {
      newPlan.PointInTime = moment.utc(machines.point, cobbledDateFormat).format(UTCFormatWithSec);
      newPlan.PointInTimeUTC = moment.utc(machines.point, cobbledDateFormat).format(UTCFormatWithSecZ);
    }

    if (
      type === PlanTypes.RestoreVMwareToCustom ||
      (newPlan.HostType === HostTypeNumbers.HyperV && newPlan.RestorePlanType === RestorePlanType.VMToCloud)
    ) {
      let Names: string[] = machines.folders[0].split('\\').filter((s: string) => !!s);

      if (Names?.length > 5) Names = Names.slice(5);

      if (Names?.length) {
        newPlan.HostName = isCbf ? Names[0].toLowerCase() : Names[0] || '';
        newPlan.VmName = Names[1] || '';
      }

      return;
    }

    delete newPlan.HostName;
    delete newPlan.VmName;

    const machinesMap: { [key: string]: MachineFormatInPlan } = {};

    machines.folders.forEach((path: string) => {
      let pathSlices = path.split('\\').filter((s: string) => !!s);

      if (pathSlices?.length > 5) pathSlices = pathSlices.slice(5);

      if (!machinesMap[pathSlices[1]]) {
        if (type === PlanTypes.RestoreProxmox) {
          const nameStrArray = pathSlices[1].split(' ');
          let vmName = nameStrArray.slice(1).join(' ');

          vmName = vmName.slice(1, vmName.length - 1);

          machinesMap[pathSlices[1]] = {
            HostName: pathSlices[0],
            VMName: vmName,
            VmUUID: SupportMethodsForPlanFromSteps.encodeNumberToGuid(+nameStrArray[0]),
            ID: +nameStrArray[0],
            NewVMName: vmName,
            ExcludeDisks: []
          };
        } else {
          machinesMap[pathSlices[1]] = {
            HostName: isCbf ? pathSlices[0].toLowerCase() : pathSlices[0],
            VMName: pathSlices[1],
            NewVMName: pathSlices[1],
            ExcludeDisks: []
          };
        }
      }
    });

    if (
      !isCbf &&
      [RestorePlanType.HyperV, RestorePlanType.VMwareMachine].includes(newPlan.RestorePlanType) &&
      disks?.type === VirtualDisksSelectedType.Selected
    ) {
      disks.excludes.forEach((path: string) => {
        let pathSlices: string[] = path.split('\\').filter((s: string) => !!s);

        if (pathSlices?.length > 5) pathSlices = pathSlices.slice(5);

        if (!machinesMap[pathSlices[1]]) {
          machinesMap[pathSlices[1]] = {
            HostName: pathSlices[0],
            VMName: pathSlices[1],
            NewVMName: pathSlices[1],
            ExcludeDisks: [pathSlices[2]]
          };
        } else {
          machinesMap[pathSlices[1]].ExcludeDisks.push(pathSlices[2]);
        }
      });
    }

    newPlan.VirtualMachines = [];

    Object.keys(machinesMap).forEach((key: string) => newPlan.VirtualMachines.push(machinesMap[key]));
  }

  public static checkUnix(str: string): boolean {
    return str.startsWith('CBB_ROOT\\') || str.startsWith('CBB_VOLUMES\\') || str.startsWith('etc\\');
  }

  public static updateNewPlanByRestoreSourceStep(
    newPlan,
    step: RestoreSourceStepValue,
    isUnix = false,
    computerName = '',
    isNBF = false
  ): void {
    const join = isUnix ? '/' : '\\';
    const unixOnWindowsPC = step.folders.every((str) => !isUnix && windowsUpperCaseInvalidPath.test(str));
    const prefixWithCompName = `\\\\${computerName}\\`;
    const getModifiedPath = (str: string) => (!isUnix && str.endsWith(join) ? str.substring(0, str.length - 1) : str);

    newPlan.Versions = [];
    newPlan.Items = [];

    if (unixOnWindowsPC && newPlan.RestoreType !== RestoreType.Manually) {
      newPlan.Items = step.folders.map((f) => ({ Path: (!isNBF && !this.checkUnix(f) ? prefixWithCompName : '') + getModifiedPath(f) }));

      return;
    }

    if (newPlan.RestoreType !== RestoreType.Manually) {
      newPlan.Items = step.folders.map((f) => ({ Path: getModifiedPath(f) }));

      return;
    }

    step.folders.forEach((str) => {
      const strArr = str.split(join);
      const lastElem = strArr[strArr.length - 1];
      const existNormal = dateTimePointsReg.test(lastElem);
      const existCobble = dateTimePointsFormat.test(lastElem);
      const exist = existNormal || existCobble;
      const result: { Path?: string; ModifyDate?: string } = {};

      if (exist) {
        const newPath = strArr.splice(0, strArr.length - 1).join(join);
        result.ModifyDate = existCobble ? moment.utc(lastElem, cobbledDateFormat).format(baseDateFormatWithSeconds) + 'Z' : lastElem;
        result.Path = getModifiedPath(newPath);

        if (unixOnWindowsPC) result.Path = (!isNBF && !this.checkUnix(result.Path) ? prefixWithCompName : '') + result.Path;

        return void newPlan.Versions.push(result);
      }

      result.Path = getModifiedPath(str);

      if (unixOnWindowsPC) result.Path = (!isNBF && !this.checkUnix(result.Path) ? prefixWithCompName : '') + result.Path;

      newPlan.Items.push(result);
    });
  }

  public static updateNewPlanByBackupToRestoreStep(newPlan, step: BackupToRestoreStepValue): void {
    newPlan.BackupPlanName = step.backupPlanName;
  }

  public static updateNewPlanByScheduleStep(
    newPlan,
    step: ScheduleStepValue,
    advancedStep: ScheduleAdvancedStepValue,
    isUnix = false,
    isNBF = false
  ): void {
    const recTypeNumber = +ScheduleType[step.ScheduleType];
    if (!isUnix || isNBF) newPlan.SyncBeforeRun = step.syncBeforeRun;
    if (recTypeNumber === ScheduleType.recurring || recTypeNumber == ScheduleType.once) {
      SupportMethodsForPlanFromSteps.SetPlanScheduleSettingsAdvanced(step, newPlan, advancedStep, isUnix, true);
    } else {
      newPlan.ForceFullSchedule = { Enabled: false };
      newPlan.Schedule = { Enabled: false };
      newPlan.ScheduleDiff = { Enabled: false };
      newPlan.ScheduleTLog = { Enabled: false };
      const stopAfterDuration = moment.duration({ hours: step.stopAfterHours, minutes: step.stopAfterMinutes });
      newPlan.Schedule = {
        Enabled: recTypeNumber !== ScheduleType.noschedule,
        StopAfter: !step.StopAfterEnabled ? TimeSpanMaxValue : GetTimeSpanString(stopAfterDuration)
      };
      if (recTypeNumber == ScheduleType.instantly) newPlan.ForceFullSchedule.RecurType = RecurType.Instantly;
      newPlan.ForceMissedSchedule = !!step.ForceMissedSchedule;
      newPlan.UseDifferentialUpload = false;
    }
    SupportMethodsForPlanFromSteps.delStopAfterTickFromPlan(newPlan);
  }

  public static updateRestoreDestinationVM(newPlan, step: DestinationVMStepValue, type: PlanTypes): void {
    newPlan[newPlan.RestorePlanType === RestorePlanType.HyperV ? 'RestorePath' : 'Destination'] = step.destinationFolder;
    newPlan.Overwrite = !!step.overwriteExistingVM;
    newPlan.PowerOnAfterRestore = !!step.powerOnVM;
    newPlan.RestoreToOriginalLocation =
      type === PlanTypes.RestoreHyperVToCustom || (type === PlanTypes.RestoreHyperV && !newPlan.ImportVm)
        ? false
        : !!step.toOriginalLocation;

    if (newPlan.RestorePlanType === RestorePlanType.HyperV && step.toOriginalLocation && !newPlan.RestorePath) newPlan.RestorePath = '';

    if (
      (newPlan.RestorePlanType !== RestorePlanType.HyperV || type === PlanTypes.RestoreHyperVToCustom || step.toOriginalLocation) &&
      newPlan.RestorePlanType !== RestorePlanType.VMwareMachine &&
      newPlan.RestorePlanType !== RestorePlanType.Proxmox
    )
      return;

    const isProxmox = type === PlanTypes.RestoreProxmox;
    const machinesHash: { [key: string]: MachineFormatInPlan } = {};
    if (isProxmox) {
      newPlan.VirtualMachines?.forEach((machine) => {
        const fullName = `${machine.ID} (${machine.VMName})`;
        machine.fullName = fullName;
        machinesHash[`${machine.HostName}\\${fullName}`] = { ...machine, fullName };
      });
    } else newPlan.VirtualMachines?.forEach((machine) => (machinesHash[`${machine.HostName}\\${machine.VMName}`] = machine));

    newPlan.Datastore = step.dataStore;

    const existUniq = step.specificList?.some((names: string) => {
      const namesArr: string[] = names.split('--');
      return machinesHash[namesArr[0]] && machinesHash[namesArr[0]][isProxmox ? 'fullName' : 'VMName'] !== namesArr[1];
    });

    if (!existUniq) {
      if (isProxmox) {
        newPlan.VirtualMachines = newPlan.VirtualMachines.map(({ ID, fullName, ...machine }) => ({
          ...machine,
          NewVMName: machine.VMName,
          NewVmUUID: machine.VmUUID
        }));
      }
      return;
    }

    step.specificList.forEach((names: string) => {
      const namesArr: string[] = names.split('--');
      newPlan.VirtualMachines.forEach((m) => {
        if (namesArr[0].startsWith(m.HostName) && namesArr[0].endsWith(m[isProxmox ? 'fullName' : 'VMName'])) {
          if (!isProxmox) m.NewVMName = namesArr[1];
          else {
            const withId = PatternNameWithId.test(namesArr[1]);

            if (!withId) m.NewVMName = namesArr[1];
            else {
              const nameStrArray = namesArr[1].split(' ');
              const newName = nameStrArray.slice(1).join(' ');

              m.NewVMName = newName.slice(1, newName.length - 1);
              m.NewVmUUID = SupportMethodsForPlanFromSteps.encodeNumberToGuid(+nameStrArray[0]);
            }
          }
        }
      });
    });

    if (isProxmox) {
      newPlan.VirtualMachines = newPlan.VirtualMachines.map(({ ID, fullName, ...machine }) => machine);
    }
  }

  public static updateRestoreDestinationDB(newPlan, step: DestinationDBStepValue): void {
    newPlan.DataPath = step.destinationFolder;
    newPlan.LogPath = step.destinationFolderLog;
    newPlan.CloseConnection = step.closeConnection;
    newPlan.FileMask = step.backupTemplate || DefaultTemplateMask;
    newPlan.Overwrite = step.overwriteExisting;

    const dbHash: { [key: string]: { InstanceName: string; DatabaseName: string; NewDatabaseName?: string } } = {};
    newPlan.Databases.forEach((db) => (dbHash[`${db.InstanceName}\\${db.DatabaseName}`] = db));

    step.specificList.forEach((names: string) => {
      const namesArr: string[] = names.split('--');
      newPlan.Databases.forEach((m) => {
        if (namesArr[0].startsWith(m.InstanceName) && namesArr[0].endsWith(m.DatabaseName)) {
          m.NewDatabaseName = namesArr[1];
        }
      });
    });
  }

  public static updateRestoreDestinationFileDB(newPlan, step: DestinationDBStepValue): void {
    newPlan.Destination = step.destinationFolder;
    newPlan.CreateFolderForEachDatabase = step.createFolder;
    newPlan.FileNameTemplate = step.backupTemplate || DefaultTemplate;
  }

  public static updateVmSettings(newPlan, step: VmSettingsStepValue, partitionsStep: PartitionsStepValue, isProxmox = false): void {
    newPlan.RestoreDiskConfiguration = this.getDisksFromRestorePartitionsVM(partitionsStep);

    if (!step) return;

    const name = isProxmox ? 'RestoreToProxmox' : 'RestoreToVMware';

    if (!newPlan[name]) newPlan[name] = {};

    newPlan[name].VmName = step.vmName;
    newPlan[name].NumCPUs = step.numCPU;
    newPlan[name].MemoryMB = step.memorySize;
    newPlan[name].Datastore = step.datastore;
    newPlan[name].PowerOnAfterRestore = step.powerOn;
    newPlan[name].Overwrite = step.overwrite;

    if (isProxmox) {
      newPlan[name].Network = { Bridge: step.virtualNetwork || null, Model: step.cardModel || 0 };
      newPlan[name].VmUuid = step.vmId;
      newPlan[name].GuestOsType = step.guestOs;
      return;
    } else {
      newPlan[name].Network = step.virtualNetwork;
      newPlan[name].GuestId = step.guestOs;
    }

    newPlan[name].ThinProvisioned = step.diskProvision === ProvisionModeEnum.thin;

    if (step.diskProvision === ProvisionModeEnum.thin) newPlan[name].EagerlyScrub = null;
    if (step.diskProvision === ProvisionModeEnum.lazy) newPlan[name].EagerlyScrub = false;
    if (step.diskProvision === ProvisionModeEnum.eager) newPlan[name].EagerlyScrub = true;
  }

  public static updateVmSettingsHyperV(newPlan, step: VmSettingsStepValue, partitionsStep: PartitionsStepValue): void {
    newPlan.RestoreDiskConfiguration = this.getDisksFromRestorePartitionsVM(partitionsStep);

    if (!step) return;
    if (!newPlan.RestoreToHyperV) newPlan.RestoreToHyperV = {};

    newPlan.RestoreToHyperV.VmName = step.vmName;
    newPlan.RestoreToHyperV.NumCpu = step.numCPU;
    newPlan.RestoreToHyperV.MemoryInMb = step.memorySize;
    newPlan.RestoreToHyperV.NetworkSwitch = step.virtualNetwork;
    newPlan.RestoreToHyperV.MaxMemoryInMb = step.maxMemorySize;
    newPlan.RestoreToHyperV.MinMemoryInMb = step.minMemorySize;
    newPlan.RestoreToHyperV.Destination = step.destination;
    newPlan.RestoreToHyperV.DynamicMemoryEnabled = step.dynamic;
    newPlan.RestoreToHyperV.IsV2 = step.generation;
    newPlan.RestoreToHyperV.PowerOnVm = step.powerOn;
    newPlan.RestoreToHyperV.Overwrite = step.overwrite;
  }

  public static updateHostIbb(newPlan, step: SelectHostStepValue, isProxmox = false): void {
    if (!step) return;

    const name = isProxmox ? 'RestoreToProxmox' : 'RestoreToVMware';

    if (!newPlan[name]) newPlan[name] = {};

    newPlan[name].Host = step.server;
    newPlan[name].Login = step.login;
    newPlan[name].RestoreToHost = step.host;

    if (newPlan[name].PasswordEncrypted !== step.password) {
      newPlan[name].Password = step.password;
      delete newPlan[name].PasswordEncrypted;
    }
  }

  public static updateHost(newPlan, step: SelectHostStepValue): void {
    newPlan.Host = step.server;
    newPlan.Login = step.login;
    newPlan.Password = step.password;
    newPlan.RestoreToHost = step.host;
  }

  public static updatePlanForRestoreWithoutOpenWizard(newPlan, settings: RestoreWizardSettings, timeZoneOffset: number): void {
    const initialFormData = settings.initialFormData;
    const passStep = initialFormData.encryptionOptionsStep as EncryptionOptionsStepValue;
    const overwriteStep = initialFormData.overwriteData as { overwriteExistingFiles: boolean; restoreOnlyNewFiles: boolean };

    newPlan.ConnectionID = (initialFormData.whereBackupStep as { ConnectionID: string }).ConnectionID;
    newPlan.BackupPlanName = (initialFormData.backupToRestoreStep as { backupPlanName: string }).backupPlanName;
    newPlan.Destination = (initialFormData.destinationStep as unknown as { destinationFolder: string }).destinationFolder;
    newPlan.UseEncryption = !!passStep?.password;

    if (newPlan.UseEncryption) newPlan.EncryptionPassword = passStep?.password;
    if (newPlan.Destination) newPlan.RestoreToOriginalLocation = false;

    if (overwriteStep) {
      if (settings.type === PlanTypes.RestorePlan) {
        newPlan.SkipExistingFiles = !overwriteStep.overwriteExistingFiles;
        if (overwriteStep.overwriteExistingFiles) newPlan.OnlyNewFiles = overwriteStep.restoreOnlyNewFiles;
      } else if (overwriteStep.overwriteExistingFiles) {
        if (settings.type === PlanTypes.RestoreDiskImagePlan) newPlan.OverwriteExisting = overwriteStep.overwriteExistingFiles;
        else newPlan.Overwrite = overwriteStep.overwriteExistingFiles;
      }
    }

    this.updateLastStepsWithoutOpenWizard(newPlan, settings, timeZoneOffset);
  }

  public static updateLastStepsWithoutOpenWizard(newPlan, settings: RestoreWizardSettings, timeZoneOffset: number): void {
    const initialFormData = settings.initialFormData;
    const computer = settings.computer;
    const isUnix = Computer.isUnix(computer);
    const backupToRestoreStep = initialFormData.backupToRestoreStep as { bunchId: string };
    const computerToRestoreStep = (initialFormData?.computerToRestoreStep || {}) as ComputerToRestoreStepValue;
    const restoreSource = (initialFormData.restoreSourceStep || {}) as RestoreSourceStepValue;
    const restoreDisksStep = (initialFormData.restoreDisksStep || {}) as RestoreDisksStepValue;
    const restorePointStep = (initialFormData?.restorePointStep || {}) as RestorePointStepValue;

    newPlan.RestoreToOriginalLocation = false;
    this.updateNewPlanByRestorePointStep(newPlan, restorePointStep, {
      isNBF: settings.isNBF,
      timeZoneOffset,
      isUnix
    } as WizardHelpersSettings);

    if (settings.type === PlanTypes.RestorePlan) {
      this.updateNewPlanByRestoreSourceStep(newPlan, restoreSource, isUnix, computer.name, settings.isNBF);
    }

    if ([PlanTypes.RestoreVMware, PlanTypes.RestoreProxmox, PlanTypes.RestoreHyperV].includes(settings.type)) {
      this.updateRestoreSourceVM(newPlan, restoreSource, restoreDisksStep, settings.type, !settings.isNBF);
    }

    if (settings.type === PlanTypes.RestoreDataBasePlan || settings.type === PlanTypes.RestoreDatabaseFilesPlan) {
      if (settings.type === PlanTypes.RestoreDataBasePlan && initialFormData.sqlServerInstanceStep) {
        PlanDataFromStepsHelper.updateNewPlanBySQLServerInstance(
          newPlan,
          initialFormData.sqlServerInstanceStep as SQLServerInstanceStepValue
        );
      }

      this.updateRestoreSourceDB(newPlan, restoreSource, isUnix);
    }

    SupportMethodsForPlanFromSteps.delStopAfterTickFromPlan(newPlan);
    PlanDataFromStepsHelper.preparePlanForSending(newPlan);

    if (settings.isNBF) {
      newPlan.RestorePoint = `CBB_Archive\\${backupToRestoreStep.bunchId}`;
      if (restorePointStep.resultPoint) {
        newPlan.RestorePoint += `\\${restorePointStep.resultPoint.restorePointId}`;
      }
    } else newPlan.RestorePoint = null;

    newPlan.RestoreSourcePrefix = computerToRestoreStep.computerName;
    newPlan.GlacierRestoreTier = GlacierRestoreTiers.Standard;
    newPlan.LimitRestore = true;
    newPlan.ForceFullSchedule = { Enabled: false };
    newPlan.Schedule = { Enabled: false };
    newPlan.ScheduleDiff = { Enabled: false };
    newPlan.ScheduleTLog = { Enabled: false };
    newPlan.Notification = { GenerateReport: true, OnlyOnFailure: true, SendNotification: false, SubjectString: null };
    newPlan.Actions = { Pre: null, Post: null };
  }

  public static getDisksFromRestorePartitionsVM(partitionsStep: PartitionsStepValue): any[] {
    const capacityAdd = 589824;
    const newDisks: any[] = [];
    let capacity = 0;

    partitionsStep.restorePartitions.forEach((disk) => {
      const newValue: any = newDisks.length ? newDisks[0] : { Capacity: 0, RestoreVolumes: [], SourceDiskId: disk.diskId };

      capacity += Math.ceil((disk.startOffset + disk.length + capacityAdd) / 1024 / 1024);

      newValue.RestoreVolumes.push({
        Length: disk.length,
        ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
        SourceVolumeId: { Identity: disk.identity, Enabled: true, DiskId: disk.diskId, Supported: true },
        DestinationVolumeId: null
      });

      if (!newDisks.length) newDisks.push(newValue);
    });

    if (newDisks[0]) newDisks[0].Capacity = Math.ceil(capacity * 1024 * 1024);

    return newDisks;
  }

  private static updateRestoreToEC2(newPlan, step: TypeDataStepValue): void {
    newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToEC2;
    if (!newPlan.RestoreToEC2) newPlan.RestoreToEC2 = new RestoreToEC2Args();
    newPlan.RestoreToEC2.EC2RestoreType = step.createAMI ? EC2RestoreTypeEnum.EC2Image : EC2RestoreTypeEnum.EC2Instance;
    newPlan.DiskImageFileFormat = {
      DisplayName: 'Hyper-V Virtual Disk (VHDX-format) dynamic',
      Name: 'VHDX',
      UseBuilder: true,
      VariantName: 'dynamic'
    };
  }

  private static updateBaseRestoreToAzure(newPlan): void {
    newPlan.DiskImageRestoreType = RestoreTypeFromAgentEnum.RestoreToAzure;
    if (!newPlan.RestoreToAzure) newPlan.RestoreToAzure = new RestoreToAzureArgs();
  }

  private static updateNewNotIbbPlanByDestinationStep(newPlan, step: DestinationStepValue, needRestoreDeleted = true): void {
    newPlan.RestoreToOriginalLocation = !!step.toOriginalLocation;

    if (!step.toOriginalLocation) newPlan.Destination = step.destinationFolder;
    if (needRestoreDeleted) newPlan.RestoreDeletedFiles = !!step.restoreDeletedFiles;

    newPlan.SkipExistingFiles = !step.overwriteExistingFiles;

    if (step.overwriteExistingFiles) newPlan.OnlyNewFiles = step.restoreOnlyNewFiles;
    else newPlan.OnlyNewFiles = false;

    newPlan.RestoreNTFSPermissions = !!step.restoreNTFSPermissions;
  }

  private static getCapacity(capacity: number, minCapacity: number): number {
    return capacity ? capacity * 1024 * 1024 * 1024 + (capacity === minCapacity ? 2097152 : 0) : 0; // Plus 2mb by default for min
  }

  private static getDisksFromRestorePartitions(newPlan, step: DestinationStepValue, partitionsStep: PartitionsStepValue): any[] {
    const newDisks: any[] = [];

    partitionsStep.restorePartitions.forEach((disk) => {
      const newValue: any = newDisks.length
        ? newDisks[0]
        : { Capacity: this.getCapacity(step.capacity, step?.minCapacity), RestoreVolumes: [], SourceDiskId: disk.diskId };

      if (newPlan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.VirtualDisk) {
        const idx = this.getIdxFromSelectedPartitions(step, disk);

        if (!step.isPhysical && idx !== -1) {
          const pData = step.selectedSpecificPartitions[idx].split('--');
          const what = pData[2].split('__');
          const to = pData[0].split('__');
          newValue.DestinationDiskId = to[1];
          newValue.RestoreVolumes.push({
            Length: disk.length,
            ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
            SourceVolumeId: { Identity: what[0], Enabled: true, DiskId: what[1], Supported: disk.supported },
            DestinationVolumeId: { Identity: to[0], Enabled: true, DiskId: to[1], Supported: disk.supported }
          });
        } else {
          newValue.DestinationDiskId = step.selectedDisk;
          newValue.RestoreVolumes.push({
            Length: disk.length,
            ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
            SourceVolumeId: { Identity: disk.identity, Enabled: true, DiskId: disk.diskId, Supported: disk.supported },
            DestinationVolumeId: null
          });
        }
      } else {
        newValue.Name = step.virtualImageName;
        newValue.RestoreVolumes.push({
          Length: disk.length,
          ExcludeRules: partitionsStep.restoreConfigs[`${disk.diskId}__${disk.identity}`] || [],
          SourceVolumeId: { Identity: disk.identity, Enabled: true, DiskId: disk.diskId, Supported: disk.supported },
          DestinationVolumeId: null
        });
      }
      if (!newDisks.length) newDisks.push(newValue);
    });

    return newDisks;
  }

  private static getIdxFromSelectedPartitions(step: DestinationStepValue, disk: unknown & { identityForUiField: string }): number {
    return step.selectedSpecificPartitions && step.selectedSpecificPartitions.length
      ? step.selectedSpecificPartitions.findIndex((p) => {
          const pData = p.split('--');
          return pData.length && pData[2] && pData[2].includes(disk.identityForUiField);
        })
      : -1;
  }

  private static updateNewIbbPlanByDestinationStep(newPlan, step: DestinationStepValue): void {
    newPlan.Destination = step.destinationFolder;
    newPlan.SpecificPartitions = newPlan.DiskImageRestoreType !== RestoreTypeFromAgentEnum.VirtualDisk && !step.isPhysical;
    newPlan.OverwriteExisting = newPlan.DiskImageRestoreType === RestoreTypeFromAgentEnum.VirtualDisk;
    // newPlan.VirtualImageFileName = step.virtualImageName;
  }
}
